import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import AppNavBar from './AppNavBar';
import ArchieChat from './ArchieChat';

const PlannerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const ADMIN_EMAILS = [
    'thomasfairgrieve3@gmail.com',
    'adriandalzell@gmail.com',
    'mark.gomersall@newdur.ac.uk',
  ];

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }
      
      if (ADMIN_EMAILS.includes(user.email)) {
        setLoading(false);
        return;
      }
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const isSubscribed = data?.subscriptionStatus === 'active' ||
            data?.subscriptionTier === 'premium' ||
            data?.subscriptionTier === 'premiumPlus';
          
          if (!isSubscribed) {
            navigate('/upgrade');
            return;
          }
        } else {
          navigate('/upgrade');
          return;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sand-100 to-sand-200">
        <div className="text-ink-900 text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar currentPage="planner" />
      
      <div className="flex items-center justify-center py-12">
        <div className="max-w-6xl mx-auto px-4">

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-coral-500 leading-tight mb-8 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
              Welcome to TeAIch Planner
            </h1>
            <p className="text-xl text-ink-700">Create engaging, differentiated lesson plans in minutes</p>
          </div>

          {/* Three Action Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            
            {/* Start Planning Card */}
            <div 
              onClick={() => navigate('/planner/form')}
              className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal hover:shadow-brutal-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group flex flex-col h-full"
            >
              <div className="p-12 text-center flex flex-col flex-grow">
                <div className="mb-8">
                  <div className="w-24 h-24 mx-auto bg-gradient-to-br from-coral-300 to-coral-400 rounded-full flex items-center justify-center group-hover:from-coral-500 group-hover:to-coral-600 transition-all duration-300">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                    </svg>
                  </div>
                </div>
                <div className="flex-grow">
                  <h2 className="text-3xl font-bold text-ink-800 mb-4 group-hover:text-coral-600 transition-colors">
                    Start Planning a Lesson
                  </h2>
                  <p className="text-ink-600 text-lg leading-relaxed mb-8">
                    Tell our AI what you want to teach and get a complete lesson plan with activities, timings, and built-in differentiation.
                  </p>
                </div>
                <div className="mt-auto">
                  <div className="bg-coral-300 text-white px-8 py-4 rounded-lg font-semibold text-lg group-hover:bg-coral-500 transition-all duration-300 inline-block">
                    Start Planning →
                  </div>
                </div>
              </div>
            </div>

            {/* Resource Generator Card */}
            <div 
              onClick={() => navigate('/planner/resources')}
              className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal hover:shadow-brutal-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group flex flex-col h-full"
            >
              <div className="p-12 text-center flex flex-col flex-grow">
                <div className="mb-8">
                  <div className="w-24 h-24 mx-auto bg-gradient-to-br from-coral-300 to-coral-400 rounded-full flex items-center justify-center group-hover:from-coral-500 group-hover:to-coral-600 transition-all duration-300">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                  </div>
                </div>
                <div className="flex-grow">
                  <h2 className="text-3xl font-bold text-ink-800 mb-4 group-hover:text-coral-600 transition-colors">
                    Resource Generator
                  </h2>
                  <p className="text-ink-600 text-lg leading-relaxed mb-8">
                    Create worksheets, activities, and teaching materials with AI assistance.
                  </p>
                </div>
                <div className="mt-auto">
                  <div className="bg-coral-300 text-white px-8 py-4 rounded-lg font-semibold text-lg group-hover:bg-coral-500 transition-all duration-300 inline-block">
                    Create Resources →
                  </div>
                </div>
              </div>
            </div>

            {/* Library Card */}
            <div 
              onClick={() => navigate('/planner/library')}
              className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal hover:shadow-brutal-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group flex flex-col h-full"
            >
              <div className="p-12 text-center flex flex-col flex-grow">
                <div className="mb-8">
                  <div className="w-24 h-24 mx-auto bg-gradient-to-br from-coral-300 to-coral-400 rounded-full flex items-center justify-center group-hover:from-coral-500 group-hover:to-coral-600 transition-all duration-300">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v2"></path>
                    </svg>
                  </div>
                </div>
                <div className="flex-grow">
                  <h2 className="text-3xl font-bold text-ink-800 mb-4 group-hover:text-coral-600 transition-colors">
                    Lesson and Resource Library
                  </h2>
                  <p className="text-ink-600 text-lg leading-relaxed mb-8">
                    Access your saved lesson plans, download documents, and continue planning lesson sequences.
                  </p>
                </div>
                <div className="mt-auto">
                  <div className="bg-coral-300 text-white px-8 py-4 rounded-lg font-semibold text-lg group-hover:bg-coral-500 transition-all duration-300 inline-block">
                    View Library →
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ArchieChat />
    </div>
  );
};

export default PlannerDashboard;